import React, { useRef, useEffect } from "react";
import styles from "./styles.module.scss";
import theme from "../../../Styles/theme.scss";
import PrimaryButton from "../../PrimaryButton";
import SecondaryButton from "../../SecondaryButton";
import SensorSignal from "../../SensorSignal";
import InstallSensorLocation from "../../InstallSensorLocation";
import AltSpinner from "../../Spinners/AltSpinner";
import { useViewport } from "../../ViewportProvider";
import { INSTALL_INSTRUCTIONS } from "../../../Services/Constants";
import { InfoIcon } from "../../../Styles/Icons/DesignSystem/InfoIcon";

type SensorLabel = keyof typeof INSTALL_INSTRUCTIONS;

type Sensor = {
  label: string;
  name: string;
  video?: string;
  splinePNG?: string;
  image?: string;
  alt?: string;
  locationRequired?: boolean;
};

type SensorStatus = {
  name: string;
  online?: boolean;
  loading?: boolean;
};

type SensorCardProps = {
  sensor: Sensor;
  isHub: boolean;
  error: string | boolean;
  sensors: SensorStatus[];
  loading: boolean;
  onViewGuide: (show: boolean) => void;
  onViewTroubleShooting: () => void;
  troubleShootingLabel: string;
  showMissingLocationMessage: boolean;
  sensorLocations: Record<string, string> | null;
  // Hub-only props
  hubAttemptFailed?: boolean;
  onConnect?: () => void;
  // Sensors-only props
  connectionsChecked?: Record<string, boolean>;
  onBackButton?: (direction: string) => void;
  onNextButton?: (direction: string) => void;
  installationComplete?: boolean;
};

const SensorCard = ({
  hubAttemptFailed,
  isHub,
  error,
  sensor,
  sensors,
  loading,
  onConnect, // used on the hub screen
  onViewGuide, // used on the hub and sensors screen
  onViewTroubleShooting, // used on the sensors screen only
  troubleShootingLabel, // used on sensors screen only
  showMissingLocationMessage,
  sensorLocations,
  connectionsChecked, // used on sensors screen only
  onBackButton,
  onNextButton,
  installationComplete,
}: SensorCardProps) => {
  const instructions = INSTALL_INSTRUCTIONS[sensor?.label as SensorLabel];
  const { width } = useViewport();
  const isMobile = width <= 600;
  const isVerySmallScreen = width <= 290;
  const sensorData = sensors?.find((s: SensorStatus) => s?.name === sensor?.name);
  const showLocationError =
    sensor?.label !== "h1" &&
    showMissingLocationMessage &&
    !sensorLocations?.[sensor?.name] &&
    sensor.locationRequired === true;
  const showCannotConnectError =
    error &&
    sensor?.label !== "h1" &&
    connectionsChecked?.[sensor?.label] === true &&
    !sensorData?.online &&
    !loading;
  const isBathroomSensor = sensor?.label === "m2";
  const isMovementSensor = sensor?.label === "m1";
  const isBedroomSensor = sensor?.label === "m3";
  const isSmartPlug = sensor?.label === "p1";
  const isMainDoorSensor = sensor?.label === "d1";
  const isFridgeDoorSensor = sensor?.label === "d2";

  const connectButtonLabel = sensorData?.online ? "Connected" : "Connect me";

  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    videoRef.current?.load();
  }, [sensor.video]);

  return (
    <div
      className={
        showCannotConnectError
          ? styles.sensor_card_error
          : styles.sensor_card_default
      }
    >
      <div className={styles.top_sensor_card}>
        <div>{instructions?.title}</div>
        {(isHub && hubAttemptFailed) || (!isHub && !sensorData?.online) ? (
          <SecondaryButton
            onClick={() => onViewTroubleShooting?.()}
            style={{ borderRadius: "8px", backgroundColor: theme.brandOrange }}
          >
            {troubleShootingLabel}
          </SecondaryButton>
        ) : (
          <SensorSignal
            online={sensorData?.online}
            loading={sensorData?.loading}
          />
        )}
      </div>
      <div
        className={
          isHub
            ? styles.location_instructions_hub
            : isBathroomSensor || isBedroomSensor || isFridgeDoorSensor
              ? styles.instructions_height_large
              : isSmartPlug || isMovementSensor || isMainDoorSensor
                ? styles.instructions_height_extra_large
                : styles.location_instructions
        }
      >
        <div className={styles.location_text}>
          Placement: <span>{instructions?.locationInstructions}</span>
        </div>
        {!isHub && (
          <div
            onClick={() => onViewGuide(true)}
            className={styles.more_info_sensors}
          >
            <InfoIcon colour={theme.primary5} />
          </div>
        )}
      </div>
      <div className={styles.video_container}>
        <img
          key={sensor.name}
          src={sensor?.splinePNG}
          alt={sensor?.alt}
          style={{
            height: isMobile ? "auto" : "200px",
            width: isMobile ? "100%" : "auto",
            borderRadius: "8px",
          }}
        />
      </div>
      <InstallSensorLocation
        sensor={sensor}
        showLocationError={showLocationError}
        isHub={isHub}
      />
      {isHub && (
        <div className={styles.hub_sensor_card_buttons}>
          <PrimaryButton
            onClick={() => onViewGuide(true)}
            style={{
              width: "100%",
              marginRight: "12px",
              backgroundColor: "white",
              borderRadius: "8px",
              border: `1px solid ${theme.primary3}`,
              lineHeight: 1.75,
              padding: "6px 8px",
            }}
          >
            {isVerySmallScreen ? "Guide" : " View guide"}
          </PrimaryButton>
          <PrimaryButton
            disabled={loading}
            onClick={onConnect}
            style={{ width: "100%", borderRadius: "8px" }}
            startIcon={loading ? <AltSpinner style={{}} small /> : undefined}
          >
            {connectButtonLabel}
          </PrimaryButton>
        </div>
      )}
      {!isHub && (
        <div className={styles.sensor_card_buttons}>
          <PrimaryButton
            onClick={() => onBackButton?.("previous")}
            style={{
              width: "100%",
              marginRight: "12px",
              backgroundColor: "white",
              borderRadius: "8px",
              border: `1px solid ${theme.primary3}`,
              lineHeight: 1.75,
              padding: "6px 8px",
            }}
          >
            {"Previous"}
          </PrimaryButton>
          <PrimaryButton
            onClick={() => onNextButton?.("next")}
            style={{
              width: "100%",
              backgroundColor: installationComplete ? "white" : theme.primary3,
              borderRadius: "8px",
              border: `1px solid ${theme.primary3}`,
              lineHeight: 1.75,
              padding: "6px 8px",
            }}
          >
            {"Next sensor"}
          </PrimaryButton>
        </div>
      )}

      {showLocationError ? (
        <div className={styles.error_container}>
          <p className={styles.error_message}>
            Please select a location for this sensor
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default SensorCard;
